import { colors as themeColors } from '../../../utils/theme';
import { useState, useEffect, useRef, useMemo } from 'react';
import { Box, Paper, Typography, TextField, Button, IconButton, useMediaQuery, useTheme, Grid, Divider, Snackbar, Alert, Dialog, DialogTitle, DialogContent, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { DataGrid } from '@mui/x-data-grid';
import DropdownProductCode from './DropdownProductCode';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import EditIcon from '@mui/icons-material/Edit';
import Dropdown from '../../../components/Dropdown';
import { useVisibleColumns } from '../../../hooks/useVisibleColumns';
import { useUserDefine } from '../../../context/UserDefineContext';
import { COL, DEFAULT_LABELS } from '../../../constants/columnConstants';
import { useStockValidation } from '../../../hooks/useStockValidation';
import { colors } from "../../../utils/theme";

// "Apply in Amt = Pkg*Qty*Rate?" — when this configure is enabled, a Pkg field
// containing anything that isn't a clean number (letters/special characters) must
// make the Pkg*Qty*Rate calculation come out as 0, instead of calculateAmount()'s
// own internal `Number(pkg) || 1` silently promoting invalid input back to 1.
// Blank/null/undefined Pkg is untouched — it still defaults to 1 (existing
// behavior for a fresh row before the user has typed a Pkg value).
const isGarbagePkg = (rawPkg) =>
    rawPkg !== '' && rawPkg !== null && rawPkg !== undefined && Number.isNaN(Number(rawPkg));

const getAmountCalcSafe = (qty, rate, rawPkg, disc, isApplyPkgInAmtEnabled, calculateAmount) => {
    if (isApplyPkgInAmtEnabled && isGarbagePkg(rawPkg)) {
        return { gross: 0, taxable: 0 };
    }
    return calculateAmount(qty, rate, rawPkg, disc);
};

const ProductDetails = ({ initialData, onNext, clientData, challanIds, connectionString, mode, onRowsChange }) => {
    const { user, selectedCompany, loading } = useAuth();
    const { isColVisible, getColLabel, getColRow } = useVisibleColumns('PROFORMA');
    // RupessToBeAsk=1 ("Rupees") -> only the Oth field itself is shown (as before).
    // RupessToBeAsk=0 ("Percentage") -> also show its disabled, read-only Amount field.
    const isOthRupeesMode = (colNo) => Boolean(getColRow(colNo)?.RupessToBeAsk);
    // Oth1→Oth7: each one's amount is applied in order on top of the running
    // TaxableAmt (qty*rate*(1-disc/100) as the starting base) — Percentage mode
    // takes its % against the running total so far (not the original base),
    // Rupees mode adds/subtracts a flat value; AddLess=1 means subtract, 0 means add.
    const OTH_FIELDS = [
        { field: 'oth1', col: COL.OTH1 },
        { field: 'oth2', col: COL.OTH2 },
        { field: 'oth3', col: COL.OTH3 },
        { field: 'oth4', col: COL.OTH4 },
        { field: 'oth5', col: COL.OTH5 },
        { field: 'oth6', col: COL.OTH6 },
        { field: 'oth7', col: COL.OTH7 },
    ];
    // Applies Oth1→Oth7 in order on top of `base`, returning both the final
    // taxable amount AND each Oth's own computed rupee contribution (so the
    // disabled "Oth N Amount" field — oth{N}Amt — has something to show in
    // Percentage mode). Used both for the live preview while editing and for
    // the actual Add/Update Product commit, so the two never disagree.
    const applyOthCharges = (base, rowState) => {
        let running = base;
        const amounts = {};
        OTH_FIELDS.forEach(({ field, col }) => {
            const amtField = `${field}Amt`;
            const row = getColRow(col);
            const isRupees = Boolean(row?.RupessToBeAsk);
            const isMinus = Boolean(row?.AddLess);
            // Rupees mode: OthN itself is left 0 in the DB (decimal(5,2), not
            // meant to hold a rupee amount) — the real typed value lives in
            // OthNAmt, so that's the source of truth to read back here too.
            const raw = isRupees ? rowState[amtField] : rowState[field];
            const val = Number(raw);
            if (!raw || Number.isNaN(val) || val === 0) {
                amounts[amtField] = null;
                return;
            }
            const contribution = isRupees ? val : (running * val) / 100;
            // Rupees mode: amtField IS the field the user is actively typing into
            // (othDisplayField routes it there) — reformatting it with toFixed(2)
            // on every keystroke changes the string under the cursor and resets
            // cursor position to the end. Keep the raw typed text as-is; it's only
            // ever Number()'d downstream, so the unformatted string is fine.
            // Percentage mode's amtField is a separate disabled/read-only display,
            // so reformatting it is safe and keeps it looking like a computed amount.
            amounts[amtField] = isRupees ? raw : contribution.toFixed(2);
            running = isMinus ? running - contribution : running + contribution;
        });
        return { taxableAmt: running, amounts };
    };
    // Recomputes base=qty*rate*(1-disc/100) + Oth1→Oth7 from whatever's currently
    // in `updated`, returning the fields to merge back into state. Shared by both
    // the Oth field handler and the Qty/Rate/Disc handlers so TaxableAmt/amount
    // stay Oth-adjusted live, no matter which of the four fields just changed.
    const recomputeTaxable = (updated) => {
        const qty = Number(updated.qty) || 0;
        const rate = Number(updated.rate) || 0;
        const disc = Number(updated.disc) || 0;
        // Use the same Pkg-aware calc as Add Product/handleNext (getAmountCalcSafe)
        // instead of raw qty*rate*(1-disc/100) — otherwise live Oth1-7 typing
        // ignores Pkg*Qty*Rate when "Apply in Amt = Pkg*Qty*Rate?" is enabled.
        const base = getAmountCalcSafe(qty, rate, updated.pkg, disc, isApplyPkgInAmtEnabled, calculateAmount).taxable;
        const { taxableAmt, amounts } = applyOthCharges(base, updated);
        return { ...amounts, TaxableAmt: taxableAmt.toFixed(2), amount: taxableAmt.toFixed(2) };
    };
    // Rupees mode: OthN itself stays 0 in the DB, so the field the user
    // actually types/sees is OthNAmt directly. Percentage mode: the user
    // types the % into OthN, and OthNAmt is the computed, read-only result.
    const othDisplayField = (field, col) => isOthRupeesMode(col) ? `${field}Amt` : field;
    // Qty/Rate/Disc changing also shifts the base amount Oth1-7 are computed
    // from — reapply them live here too, instead of only on Add/Update Product.
    const handleBaseFieldChange = (field) => (e) => {
        const raw = e.target.value;
        setCurrent(prev => {
            const updated = { ...prev, [field]: raw };
            return { ...updated, ...recomputeTaxable(updated) };
        });
    };
    const { getSelectedCharges, getChargeItemId, userDefineData } = useUserDefine();
    const { isLastSaleRateEnabled, getLastSaleRate, isApplyPkgInAmtEnabled, calculateAmount , isDiscSchemeEnabled, getClientDiscScheme } = useStockValidation(connectionString, selectedCompany?.CompId);
    // getSelectedCharges() returns a brand-new array on every call (Array.filter),
    // even when userDefineData hasn't changed — used as a useEffect dependency
    // further down, an unstable reference there re-fires those effects (and their
    // setState calls) on every render, causing an infinite render loop. Memoize
    // on userDefineData (the actual stable source data) so the array reference
    // only changes when the underlying charges data actually changes.
    const additionalCharges = useMemo(() => getSelectedCharges('PROFORMA'), [userDefineData]);
    const [chargesDialogOpen, setChargesDialogOpen] = useState(false);
    const [chargesCollapsibleOpen, setChargesCollapsibleOpen] = useState(true);
    const [chargeInputs, setChargeInputs] = useState({});
    // Additional Charges contribution applied to the summary panel — populated
    // only when the user clicks Save in the Charges popup (see handleSaveCharges).
    const [chargesSummary, setChargesSummary] = useState({ total: 0, cgst: 0, sgst: 0, igst: 0 });

    // Restore chargeInputs and chargesSummary from initialData if provided (when loading an existing quotation)
    useEffect(() => {
        // API chargeInputs are already keyed by TransType (200, 201, 202, 203, 204)
        // which matches charge.TransType in additionalCharges, so no mapping needed
        if (initialData?.chargeInputs && Object.keys(initialData.chargeInputs).length > 0) {
            console.log('[ChargeInputs Debug] API chargeInputs keys:', Object.keys(initialData.chargeInputs));
            if (additionalCharges && additionalCharges.length > 0) {
                console.log('[ChargeInputs Debug] additionalCharges:', additionalCharges.map(c => ({ TransType: c.TransType, SrNo: c.SrNo, name: c.ChargeName })));
            }
            setChargeInputs(initialData.chargeInputs);
        }

        // Restore chargesSummary from charge rows in tableData (has actual calculated tax amounts)
        // This is more reliable since the API calculates taxes based on actual requirements
        if (initialData?.tableData) {
            const chargesTaxData = { total: 0, cgst: 0, sgst: 0, igst: 0 };

            initialData.tableData.forEach(row => {
                const transType = row.TransType != null ? parseInt(row.TransType) : 100;
                const isCharge = transType >= 200 && transType <= 204;
                if (isCharge) {
                    // Use the amount from the charge row
                    chargesTaxData.total += Number(row.amount || 0);
                    // Add the tax amounts from the charge row (already calculated by backend)
                    chargesTaxData.cgst += Number(row.CGSTAmt || 0);
                    chargesTaxData.sgst += Number(row.SGSTAmt || 0);
                    chargesTaxData.igst += Number(row.IGSTAmt || 0);
                }
            });

            console.log('[ChargesSummary Debug] Calculated from tableData:', chargesTaxData);

            if (chargesTaxData.total > 0 || chargesTaxData.cgst > 0 || chargesTaxData.sgst > 0 || chargesTaxData.igst > 0) {
                setChargesSummary(chargesTaxData);
            }
        }
        // Depend on the specific fields read above, not the whole initialData object —
        // initialData is a fresh object literal from the parent on every render
        // (formData.product || {}), so depending on it directly re-fires this effect
        // (and its setChargesSummary call) on every parent re-render, not just when
        // the actual saved charge data changes.
    }, [initialData?.chargeInputs, initialData?.tableData]);

    // Populate each charge slot's editable state (Amount, and Tax when TaxEnabled)
    // fresh every time the popup opens — default Tax is whatever was saved for
    // that slot in UserDefineColumns (charge.TaxId). Preserve amount if already restored from initialData.
    useEffect(() => {
        if (!chargesDialogOpen) return;
        setChargeInputs(prev => {
            const next = { ...prev };
            additionalCharges.forEach(charge => {
                const key = charge.TransType;
                if (!next[key]) {
                    // Only set empty amount if not already populated (e.g., from initialData restore)
                    next[key] = {
                        amount: '',
                        taxId: charge.TaxId ?? null,
                    };
                } else {
                    // Preserve existing amount, but update taxId to the configured one if not already set
                    if (next[key].taxId === null && charge.TaxId !== null) {
                        next[key].taxId = charge.TaxId;
                    }
                }
            });
            return next;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chargesDialogOpen, additionalCharges]);

    const updateChargeInput = (key, patch) => {
        setChargeInputs(prev => ({
            ...prev,
            [key]: { ...prev[key], ...patch },
        }));
    };
    const companyId = selectedCompany?.CompId || null;
    console.log("ididi", initialData)
    const state = clientData?.details?.['State'] || '';
    const compState = selectedCompany?.State || '';
    console.log('challanIds:', challanIds);
    console.log(`state: ${state} || compState: ${compState}`);
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down('md'));
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [rows, setRows] = useState([]);
    const [nextSrNo, setNextSrNo] = useState(1);
    const [current, setCurrent] = useState({ Id: '', productCode: '', description: '', UnitId: '', unit: '', qty: 1, rate: 0, disc: 0, tax: 0, batchNo: '', expDate: '', serialNo: '', amount: '', taxAmount: '', remark: '', CGST: '0.00', SGST: '0.00', IGST: '0.00', UTGST: '0.00', GSTCess: '0.00', CGSTAmt: '0.00', SGSTAmt: '0.00', IGSTAmt: '0.00', TaxableAmt: '0.00', TaxId: null, TransType: 100, challanDtsID: null, challanID: null, soId: null, soDtsID: null, refNo: '', refDate: '', refQty: '', type: '', size: '', color: '', freeQty: '', per: '', pkg: '', oth1: '', oth2: '', oth3: '', GSTOth: '0.00', poQty: '', ex4: '', ex5: '', exQty6: '', serialNo: '', oth4: '', oth5: '', oth6: '', oth7: '' });
    const [totals, setTotals] = useState({ basic: 0, grossBasic: 0, discount: 0, cgst: 0, sgst: 0, igst: 0, roundOff: 0, total: 0 });
    const [errors, setErrors] = useState({});
    const [editIndex, setEditIndex] = useState(null);
    const [allProducts, setAllProducts] = useState([]);
    const [unitOptions, setUnitOptions] = useState([]);
    const [colorMap, setColorMap] = useState({});
    const [sizeMap, setSizeMap] = useState({});
    const [originalEditQty, setOriginalEditQty] = useState(null);
    const [isEditingMode, setIsEditingMode] = useState(false);

    const [isInitialized, setIsInitialized] = useState(false);

    const [gsts, setGsts] = useState([]);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const isFromSO = rows.some(row => row.soId != null || row.soDtsID != null || row.qoutDtsID != null);

    const isProforma = mode === 'proforma';

    const isProductLocked = isProforma || isFromSO;

    const requiredAsteriskStyle = { '& .MuiFormLabel-asterisk': { color: 'red', }, };

    const API_BASE = `${connectionString}/api`;


    const transformGSTs = data => Array.isArray(data) ? data.map(i => ({ Id: i.TaxID, value: i.Taxcode })) : [];


    // Track if component has finished initializing
    const hasInitializedRef = useRef(false);

    // When true, the next run of the live-calculation effect is skipped —
    // set right before a row's DB values are loaded into `current` (editRow),
    // so entering edit mode doesn't immediately overwrite the stored amount.
    const skipNextCalcRef = useRef(false);

    // Ref to the Product Code field for auto-focus
    const productCodeRef = useRef(null);

    // Notify parent when rows change — but ONLY after initialization is done
    useEffect(() => {
        if (!hasInitializedRef.current) return; // Don't fire on first empty render
        if (onRowsChange) {
            onRowsChange(rows);
        }
    }, [rows]);

    // Reset isInitialized when coming back with data but rows are empty
    useEffect(() => {
        if (initialData?.tableData?.length && rows.length === 0 && hasInitializedRef.current) {
            setIsInitialized(false);
        }
    }, [initialData?.tableData]);

    // For add mode — no initialData, so mark as initialized immediately
    useEffect(() => {
        if (!initialData?.tableData?.length) {
            hasInitializedRef.current = true;
        }
    }, []);

    const Hlp_Fn_GST = async () => {
        try {
            const { data } = await axios.get(`${API_BASE}/product-get-data/api/gst?companyId=${companyId}`);
            console.log(data)
            setGsts(transformGSTs(data));
        } catch (e) {
            console.error('GST lookup failed', e);
            setSnackbar({ open: true, message: 'GST lookup failed.', severity: 'error' });
            setGsts([]);
        }
    };

    useEffect(() => {
        Hlp_Fn_GST();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const unitsRes = await axios.get(`${connectionString}/api/unit/retrive`, { params: { companyId } });
                setUnitOptions(unitsRes.data); // Expecting [{ UnitId: 43, unit: "NOS" }, ...]
                const [cRes, sRes] = await Promise.all([axios.get(`${connectionString}/api/colormaster`), axios.get(`${connectionString}/api/sizemaster`)]);
                const cm = {}, sm = {};
                cRes.data.forEach(c => (cm[c.colorId] = { name: c.itemColor, code: c.colorCode }));
                sRes.data.forEach(s => (sm[s.sizeId] = { name: s.itemSize, code: s.sizeCode }));
                setColorMap(cm);
                setSizeMap(sm);
            } catch (error) {
                console.error('Error fetching product or unit data:', error);
            }
        };

        fetchData();
        loadAllProducts();
    }, []);

    const handleProductAdded = async () => {
        // Reload units when product is added
        try {
            const unitsRes = await axios.get(`${connectionString}/api/unit/retrive`, {
                params: { companyId }
            });
            setUnitOptions(unitsRes.data);

            // Also reload products
            await loadAllProducts();
        } catch (error) {
            console.error('Error refreshing data:', error);
        }
    };

    const loadAllProducts = async () => {
        try {
            const res = await axios.get(`${connectionString}/api/productdata/retrive`, { params: { companyId, mode: 'SALE' } });
            setAllProducts(res.data || []);
        } catch (e) {
            console.error('Error fetching products:', e);
        }
    };

    useEffect(() => {
        if (isInitialized || !initialData?.tableData || !Array.isArray(initialData.tableData) || !unitOptions.length || !allProducts.length) {
            return;
        }

        // The products grid (`rows`) must only ever hold normal product rows.
        // Additional Charges (TransType 200-204) and RoundOff (300) are restored
        // separately (chargeInputs / chargesSummary) and re-added at submit time —
        // including them here as well would duplicate them in the outgoing payload.
        const regularRows = initialData.tableData.filter(r => r.TransType !== 200 && r.TransType !== 201 && r.TransType !== 202 && r.TransType !== 203 && r.TransType !== 204 && r.TransType !== 300);

        const initialRows = regularRows.map((r, index) => {
            const srNoValue = regularRows.length - index;
            const qty = Number(r.qty) || 0;
            const rate = Number(r.rate) || 0;
            const disc = Number(r.disc) || 0;
            // Prefer the exact amount stored in the database over recomputing it —
            // recomputing here can silently diverge from what was actually saved.
            // Only fall back to qty*rate*(1-disc/100) when the DB has no stored amount.
            const storedAmt = r.TaxableAmt ?? r.amount;
            const hasStoredAmt = storedAmt != null && storedAmt !== '' && !Number.isNaN(Number(storedAmt));
            const taxableAmt = hasStoredAmt ? Number(storedAmt) : qty * rate * (1 - disc / 100);

            let taxId = r.taxId || r.TaxId || null;
            const product = allProducts.find(p => p.Id === r.productId || p.itemCode === r.productCode);

            //  GST lookup
            const matchedGST = gsts.find(g => String(g.Id) === String(taxId));

            //  final %
            let tax = matchedGST ? parseFloat(matchedGST.value) : Number(r.GST) || (Number(r.CGST) + Number(r.SGST)) || Number(r.IGST) || 0;

            // let tax = Number(r.tax) || 0;
            let taxAmount = taxableAmt * (tax / 100);
            let cgst = '0.00', sgst = '0.00', igst = '0.00';
            let cgstAmt = '0.00', sgstAmt = '0.00', igstAmt = '0.00';

            const matchedUnit =
                unitOptions.find(u => String(u.UnitId) === String(r.UnitId)) ||
                unitOptions.find(u => u.unit?.toLowerCase() === String(r.unit).toLowerCase()) ||
                (product ? unitOptions.find(u =>
                    String(u.UnitId) === String(product.UnitId) ||
                    u.unit?.toLowerCase() === String(product.Unit?.toLowerCase())
                ) : null);

            if (product) {
                tax = tax;       //  backend value
                taxId = r.taxId || r.TaxId || null;        //  backend value
                taxAmount = taxableAmt * (tax / 100);
                cgst = state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00';
                sgst = state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00';
                igst = state?.toLowerCase() !== compState?.toLowerCase() ? tax.toFixed(2) : '0.00';
                cgstAmt = state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00';
                sgstAmt = state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00';
                igstAmt = state?.toLowerCase() !== compState?.toLowerCase() ? taxAmount.toFixed(2) : '0.00';
            } else {
                taxAmount = taxableAmt * (tax / 100);
                cgst = Number(r.CGST || (state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2) : 0)).toFixed(2);
                sgst = Number(r.SGST || (state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2) : 0)).toFixed(2);
                igst = Number(r.IGST || (state?.toLowerCase() !== compState?.toLowerCase() ? tax : 0)).toFixed(2);
                cgstAmt = Number(r.CGSTAmt || (state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2) : 0)).toFixed(2);
                sgstAmt = Number(r.SGSTAmt || (state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2) : 0)).toFixed(2);
                igstAmt = Number(r.IGSTAmt || (state?.toLowerCase() !== compState?.toLowerCase() ? taxAmount : 0)).toFixed(2);
            }

            return {
                ...r,
                productId: r.productId || r.Id || r.id || '',
                UnitId: matchedUnit ? String(matchedUnit.UnitId) : (product?.UnitId || ''),
                unit: matchedUnit ? matchedUnit.unit : (product?.Unit || ''),
                qty,
                rate,
                disc,
                tax,
                TaxableAmt: taxableAmt.toFixed(2),
                taxAmount: Number(r.taxAmount) || taxAmount.toFixed(2),
                amount: taxableAmt.toFixed(2),
                CGST: cgst,
                SGST: sgst,
                IGST: igst,
                CGSTAmt: cgstAmt,
                SGSTAmt: sgstAmt,
                IGSTAmt: igstAmt,
                UTGST: String(r.UTGST || '0.00'),
                GSTCess: String(r.GSTCess || '0.00'),
                TaxId: taxId,
                srNo: srNoValue,
                challanDtsID: r.challanDtsID || null,
                challanID: r.challanID || null,
                qoutID: r.qoutID || null,
                qoutDtsID: r.qoutDtsID || null,
                soId: r.soId || null,
                soDtsID: r.soDtsID || null, poQty: r.poQty || null, ex4: r.ex4 || null, ex5: r.ex5 || null, exQty6: r.exQty6 || null, serialNo: r.serialNo || null, oth4: r.oth4 || null, oth5: r.oth5 || null, oth6: r.oth6 || null, oth7: r.oth7 || null };
        });

        setRows(initialRows);
        setNextSrNo(initialRows.length + 1);
        setIsInitialized(true);
        hasInitializedRef.current = true; // ← for lifting rows state up 
    }, [initialData?.tableData, unitOptions, allProducts, state, compState , isInitialized]);

    useEffect(() => {
        // `current` was just loaded from a saved row (editRow) — skip this one
        // pass so the DB-stored amount isn't immediately overwritten before the
        // user has actually changed anything.
        if (skipNextCalcRef.current) {
            skipNextCalcRef.current = false;
            return;
        }

        const qty = Number(current.qty) || 0;
        const rate = Number(current.rate) || 0;
        const disc = Number(current.disc) || 0;
        const tax = Number(current.tax) || 0;
        const baseTaxableAmt = getAmountCalcSafe(qty, rate, current.pkg, disc, isApplyPkgInAmtEnabled, calculateAmount).taxable;
        // Oth1→Oth7 are applied on top of this base TaxableAmt so the live preview
        // (and the oth{N}Amt "Amount" fields) stay in sync as the user types.
        const { taxableAmt, amounts } = applyOthCharges(baseTaxableAmt, current);
        const taxAmount = taxableAmt * (tax / 100);
        setCurrent(prev => ({
            ...prev,
            ...amounts,
            TaxableAmt: taxableAmt.toFixed(2),
            amount: taxableAmt.toFixed(2),
            taxAmount: taxAmount.toFixed(2),
            CGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
            SGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
            IGST: state?.toLowerCase() !== compState?.toLowerCase() ? tax.toFixed(2) : '0.00',
            CGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
            SGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
            IGSTAmt: state?.toLowerCase() !== compState?.toLowerCase() ? taxAmount.toFixed(2) : '0.00'
        }));
    }, [current.qty, current.rate, current.pkg, current.disc, current.tax, current.oth1, current.oth2, current.oth3, current.oth4, current.oth5, current.oth6, current.oth7, state, compState, isApplyPkgInAmtEnabled, calculateAmount]);

    useEffect(() => {
        const n = v => { const num = Number(v || 0); return Number.isNaN(num) ? 0 : num; };
        const isSameState = state?.toLowerCase() === compState?.toLowerCase();

        let basicAmountSum = 0;
        let grossAmountSum = 0;
        let discountTotal = 0;
        let cgstTotal = 0;
        let sgstTotal = 0;
        let igstTotal = 0;

        const regularRows = rows.filter(r => r.TransType !== 200 && r.TransType !== 201 && r.TransType !== 202 && r.TransType !== 203 && r.TransType !== 204 && r.TransType !== 300);

        regularRows.forEach(r => {
            const qty = n(r.qty);
            const rate = n(r.rate);
            const gross = getAmountCalcSafe(qty, rate, r.pkg, 0, isApplyPkgInAmtEnabled, calculateAmount).gross;
            grossAmountSum += gross;
            const taxable = n(r.amount || r.TaxableAmt);
            // Disc% is against gross only — independent of any Oth1-7
            // adjustment baked into `taxable`, which is the final amount.
            const discAmtRow = gross * (n(r.disc) / 100);
            const taxPct = n(r.tax);

            const cgstAmtRow = (r.CGSTAmt != null && r.CGSTAmt !== '') ? n(r.CGSTAmt) : (isSameState ? taxable * (taxPct / 100) / 2 : 0);
            const sgstAmtRow = (r.SGSTAmt != null && r.SGSTAmt !== '') ? n(r.SGSTAmt) : (isSameState ? taxable * (taxPct / 100) / 2 : 0);
            const igstAmtRow = (r.IGSTAmt != null && r.IGSTAmt !== '') ? n(r.IGSTAmt) : (isSameState ? 0 : taxable * (taxPct / 100));

            basicAmountSum += taxable;
            discountTotal += discAmtRow;
            cgstTotal += cgstAmtRow;
            sgstTotal += sgstAmtRow;
            igstTotal += igstAmtRow;
        });

        const totalTax = cgstTotal + sgstTotal + igstTotal;
        // Additional Charges must be folded in before rounding, so Round Off reflects them
        const chargesTotal = Number(chargesSummary.total || 0)
            + Number(chargesSummary.cgst || 0)
            + Number(chargesSummary.sgst || 0)
            + Number(chargesSummary.igst || 0);
        const finalAmount = basicAmountSum + totalTax + chargesTotal;

        // const roundedFinal = Math.ceil(finalAmount);
        // const roundOff = Number((roundedFinal - finalAmount).toFixed(2));
        const decimalPart = finalAmount - Math.floor(finalAmount);

        const roundedFinal = decimalPart >= 0.5 ? Math.ceil(finalAmount) : Math.floor(finalAmount);

        const roundOff = Number((roundedFinal - finalAmount).toFixed(2));

        // Guard: never let a NaN reach state — Tax Free (0%) rows must always total to 0, not NaN
        const safe = v => (Number.isNaN(v) ? 0 : v);

        setTotals({
            basic: safe(basicAmountSum).toFixed(2),
            grossBasic: safe(grossAmountSum).toFixed(2),
            discount: safe(discountTotal).toFixed(2),
            cgst: safe(cgstTotal).toFixed(2),
            sgst: safe(sgstTotal).toFixed(2),
            igst: safe(igstTotal).toFixed(2),
            roundOff: safe(roundOff).toFixed(2),
            total: safe(roundedFinal).toFixed(2)
        });
    }, [rows, state, compState, chargesSummary]);

    // Additional Charges: given a charge and its current popup input, compute
    // the charge amount, the tax on it (only when TaxEnabled), and the final
    // (tax-inclusive) amount — this is what "Final Amount" displays live.
    const getChargeFinalAmounts = (charge, input) => {
        const chargeAmount = Number(input?.amount) || 0;
        if (!charge.TaxEnabled) {
            return { chargeAmount, taxPercent: 0, taxAmount: 0, finalAmount: chargeAmount };
        }
        const selectedGST = gsts.find(g => String(g.Id) === String(input?.taxId));
        const taxPercent = parseFloat(selectedGST?.value) || 0;
        const taxAmount = chargeAmount * taxPercent / 100;
        return { chargeAmount, taxPercent, taxAmount, finalAmount: chargeAmount + taxAmount };
    };

    // Additional Charges: on Save, fold every charge's final amount into the
    // Total Amount shown in the summary, and its tax (if any) into CGST+SGST
    // (same state) or IGST (different state) — same split rule the totals
    // useEffect above already uses for product rows.
    const handleSaveCharges = () => {
        const isSameState = state?.toLowerCase() === compState?.toLowerCase();
        let addTotal = 0, addCgst = 0, addSgst = 0, addIgst = 0;
        additionalCharges.forEach(charge => {
            const key = charge.TransType;
            const input = chargeInputs[key];
            if (!input || !input.amount) return;
            const amount = Number(input.amount) || 0;
            const { taxAmount } = getChargeFinalAmounts(charge, input);
            addTotal += amount;
            if (charge.TaxEnabled) {
                if (isSameState) {
                    addCgst += taxAmount / 2;
                    addSgst += taxAmount / 2;
                } else {
                    addIgst += taxAmount;
                }
            }
        });
        setChargesSummary({ total: addTotal, cgst: addCgst, sgst: addSgst, igst: addIgst });
        setChargesDialogOpen(false);
    };

    // Auto-sync chargesSummary with chargeInputs whenever charges are entered
    // This ensures charges are calculated in real-time without needing a Save button
    useEffect(() => {
        const isSameState = state?.toLowerCase() === compState?.toLowerCase();
        let addTotal = 0, addCgst = 0, addSgst = 0, addIgst = 0;
        additionalCharges.forEach(charge => {
            const key = charge.TransType;
            const input = chargeInputs[key];
            if (!input || !input.amount) return;
            const amount = Number(input.amount) || 0;
            const { taxAmount } = getChargeFinalAmounts(charge, input);
            addTotal += amount;
            if (charge.TaxEnabled) {
                if (isSameState) {
                    addCgst += taxAmount / 2;
                    addSgst += taxAmount / 2;
                } else {
                    addIgst += taxAmount;
                }
            }
        });
        setChargesSummary({ total: addTotal, cgst: addCgst, sgst: addSgst, igst: addIgst });
    }, [chargeInputs, additionalCharges, state, compState, gsts]);

    // const handleChange = field => e => {
    //     if (field === 'UnitId') {
    //         const selectedUnit = unitOptions.find(u => u.UnitId === Number(e.target.value));
    //         setCurrent(prev => ({
    //             ...prev,
    //             UnitId: e.target.value,
    //             unit: selectedUnit ? selectedUnit.unit : ''
    //         }));
    //     } else {
    //         setCurrent(prev => ({ ...prev, [field]: e.target.value }));
    //     }
    // };

    const handleChange = field => e => {
        if (field === 'UnitId') {
            const selectedUnit = unitOptions.find(u => u.UnitId === Number(e.target.value));
            setCurrent(prev => ({
                ...prev,
                UnitId: e.target.value,
                unit: selectedUnit ? selectedUnit.unit : ''
            }));
        } else if (field === 'qty') {
            let value = e.target.value;
            let numeric = Number(value) || 0;

            // Only restrict qty if soId OR soDtsID exist
            if ((current.soId != null || current.soDtsID != null || current.qoutDtsID != null) && editIndex != null && originalEditQty != null && numeric > originalEditQty) {
                numeric = originalEditQty;
            }

            setCurrent(prev => {
                const updated = {
                    ...prev,
                    qty: numeric
                };
                // Qty shifts the base amount Oth1-7 are computed from — reapply
                // them live here too, same as Disc already does via handleBaseFieldChange.
                return { ...updated, ...recomputeTaxable(updated) };
            });
        } else if (field === 'rate' || field === 'pkg') {
            // Rate/Pkg also shift the base amount Oth1-7 are computed from —
            // reapply them live here too, same as Disc already does via handleBaseFieldChange.
            setCurrent(prev => {
                const updated = { ...prev, [field]: e.target.value };
                return { ...updated, ...recomputeTaxable(updated) };
            });
        } else {
            setCurrent(prev => ({ ...prev, [field]: e.target.value }));
        }
    };

    // Oth1-7 input: numbers only, clamped to [0,100] in Percentage mode. The
    // actual TaxableAmt/oth{N}Amt recompute happens in the live-calc useEffect
    // above (which has oth1..oth7 in its dependency array), so this just needs
    // to validate and write the raw field.
    const handleOthChange = (field, col) => (e) => {
        let raw = e.target.value;
        if (raw !== '' && !/^\d*\.?\d*$/.test(raw)) return;
        if (!isOthRupeesMode(col) && raw !== '') {
            const num = Number(raw);
            if (num > 100) raw = '100';
            if (num < 0) raw = '0';
        }
        const targetKey = othDisplayField(field, col);
        setCurrent(prev => {
            const updated = { ...prev, [targetKey]: raw };
            return { ...updated, ...recomputeTaxable(updated) };
        });
    };

    const handleSelect = async product => {
        const matchedUnit = unitOptions.find(opt => {
            if (product.UnitId && opt.UnitId === product.UnitId) return true;
            if (product.Unit && opt.unit?.toLowerCase() === product.Unit.toLowerCase()) return true;
            if (product.unit && opt.unit?.toLowerCase() === product.unit.toLowerCase()) return true;
            return false;
        });
        const qty = 1;
        let rate = Number(product.Rate) || 0;
        let disc = Number(product.Discount) || 0;

        // Fetch Last Sale Rate if enabled
        if (isLastSaleRateEnabled) {
            console.log('💾 ProformaInvoice_Stepper: Fetching last sale rate for product:', product.Id, 'party:', clientData?.clientId);
            const lastSaleData = await getLastSaleRate(product.Id, clientData?.clientId);
            if (lastSaleData.rate !== null) {
                rate = lastSaleData.rate;
                console.log('✅ ProformaInvoice_Stepper: Last sale rate fetched:', rate);
            }
            if (lastSaleData.disc !== null) {
                disc = lastSaleData.disc;
                console.log('✅ ProformaInvoice_Stepper: Last sale disc fetched:', disc);
            }
        }

        // Resolve tax % from gsts array using TaxId, with fallback to product.GST
        // (handles Tax Free products where product.GST is a label like "Tax Free", not a number)
        const matchedGST = gsts.find(g => String(g.Id) === String(product.TaxId));
        const tax = matchedGST
            ? (parseFloat(matchedGST.value) || 0)
            : parseFloat(product.GST) || 0;
        const taxId = product.TaxId || null;

        // "Disc Scheme" — Disc is taken from clientmaster.Disc; Oth1/Oth2 from clientmaster.Disc1/Disc2.
        // Oth1 and Oth2 are checked independently: each is filled only if it is
        // enabled, Percentage (not Rupees) and minus (AddLess). Empty/null -> 0.
        let discSchemeOth = null;
        if (isDiscSchemeEnabled && clientData?.clientId) {
            const scheme = await getClientDiscScheme(clientData.clientId);
            disc = scheme.disc || 0;
            const oth1Qualifies = isColVisible(COL.OTH1) && !isOthRupeesMode(COL.OTH1) && Boolean(getColRow(COL.OTH1)?.AddLess);
            const oth2Qualifies = isColVisible(COL.OTH2) && !isOthRupeesMode(COL.OTH2) && Boolean(getColRow(COL.OTH2)?.AddLess);
            if (oth1Qualifies || oth2Qualifies) {
                discSchemeOth = {
                    oth1: oth1Qualifies ? String(scheme.disc1 || 0) : '',
                    oth2: oth2Qualifies ? String(scheme.disc2 || 0) : '',
                };
            }
        }

        const taxableAmt = qty * rate * (1 - disc / 100);
        const taxAmount = taxableAmt * (tax / 100);
        setCurrent({
            Id: product.Id?.toString() || '',
            productCode: product.itemCode || '',
            description: product.description || '',
            UnitId: matchedUnit ? String(matchedUnit.UnitId) : '',
            unit: matchedUnit ? matchedUnit.unit : '',
            qty,
            rate,
            disc,
            tax,
            batchNo: '',
            expDate: '',
            serialNo: '',
            amount: taxableAmt.toFixed(2),
            taxAmount: taxAmount.toFixed(2),
            remark: '',
            TaxId: taxId,
            CGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
            SGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
            IGST: state?.toLowerCase() !== compState?.toLowerCase() ? tax.toFixed(2) : '0.00',
            UTGST: '0.00',
            GSTCess: '0.00',
            CGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
            SGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
            IGSTAmt: state?.toLowerCase() !== compState?.toLowerCase() ? taxAmount.toFixed(2) : '0.00',
            TaxableAmt: taxableAmt.toFixed(2),
            challanDtsID: null,
            challanID: null,
            qoutID: null,
            qoutDtsID: null,
            soId: null,
            soDtsID: null,
            refNo: '',
            refDate: '',
            refQty: '',
            type: '',
            size: '',
            color: '',
            freeQty: '',
            per: '',
            pkg: '',
            oth1: discSchemeOth ? discSchemeOth.oth1 : '',
            oth2: discSchemeOth ? discSchemeOth.oth2 : '',
            oth3: '',
            GSTOth: '0.00',
            PONo: '',
            PODate: '',
            poQty: '',
            ex4: '',
            ex5: '',
            exQty6: '',
            serialNo: '',
            oth4: '',
            oth5: '',
            oth6: '',
            oth7: '',
            oth1Amt: null,
            oth2Amt: null,
            oth3Amt: null,
            oth4Amt: null,
            oth5Amt: null,
            oth6Amt: null,
            oth7Amt: null
        });
        // Reapply Oth1-7 on the fresh row so Oth1/Oth2 Amount and TaxableAmt reflect the Disc Scheme.
        if (discSchemeOth) {
            setCurrent(prev => ({ ...prev, ...recomputeTaxable(prev) }));
        }
        // setEditIndex(null);
        setErrors({});
    };

    const addProduct = () => {

        if (isProductLocked) return;

        const errs = {};
        const messages = new Set();
        const requiredFields = ['productCode', 'description', 'UnitId', 'qty', 'rate', 'tax'];

         // GST validation - allow null TaxId only for tax-free products (tax = 0)
        if (!current.TaxId && Number(current.tax) > 0) {
            errs.TaxId = true;
            messages.add('Please select GST before adding product.');
        }

        requiredFields.forEach(field => {
            if (!current[field] && current[field] !== 0) {
                errs[field] = 'Required';
                messages.add('All fields are required.');
            }
        });

        if (!current.qty || Number(current.qty) <= 0) {
            errs.qty = 'Must be > 0';
            messages.add('Qty must be greater than 0');
        }

        if (!current.rate || Number(current.rate) <= 0) {
            errs.rate = 'Rate must be greater than 0';
            messages.add('Rate must be greater than 0');
        }

        if (current.disc < 0) {
            errs.disc = 'Cannot be negative';
            messages.add('Discount cannot be negative');
        }

        if (current.disc > 100) {
            errs.disc = 'Cannot be more than 100';
            messages.add('Discount cannot be more than 100');
        }

        if (messages.size > 0) {
            errs.global = Array.from(messages);
            setErrors(errs);
            return;
        }

        const safeDisc = current.disc === '' || current.disc == null ? 0 : Number(current.disc);

        const qty = Number(current.qty) || 0;
        const rate = Number(current.rate) || 0;
        // const disc = Number(current.disc) || 0;
        const disc = safeDisc;
        const tax = Number(current.tax) || 0;
        const baseTaxableAmt = getAmountCalcSafe(qty, rate, current.pkg, disc, isApplyPkgInAmtEnabled, calculateAmount).taxable;
        // Oth1→Oth7 are applied on top of this base TaxableAmt before the row is committed,
        // so Add/Update Product always carries the Oth-adjusted amount, not the raw base.
        const { taxableAmt, amounts: othAmounts } = applyOthCharges(baseTaxableAmt, current);
        const taxAmount = taxableAmt * (tax / 100);

        const row = {
            ...current,
            ...othAmounts,
            productId: current.Id,
            qty,
            rate,
            disc,
            tax,
            remark: current.remark || '',
            TaxableAmt: taxableAmt.toFixed(2),
            taxAmount: taxAmount.toFixed(2),
            amount: taxableAmt.toFixed(2),
            CGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
            SGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
            IGST: state?.toLowerCase() !== compState?.toLowerCase() ? tax.toFixed(2) : '0.00',
            CGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
            SGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (taxAmount / 2).toFixed(2) : '0.00',
            IGSTAmt: state?.toLowerCase() !== compState?.toLowerCase() ? taxAmount.toFixed(2) : '0.00',
            UTGST: current.UTGST,
            GSTCess: current.GSTCess,
            TaxId: current.TaxId,
            TransType: current.TransType || 100,
            srNo: editIndex != null ? rows[editIndex].srNo : nextSrNo,
            challanDtsID: editIndex != null ? rows[editIndex].challanDtsID : null,
            challanID: editIndex != null ? rows[editIndex].challanID : null,
            qoutID: editIndex != null ? rows[editIndex].qoutID : null,
            qoutDtsID: editIndex != null ? rows[editIndex].qoutDtsID : null,
            soId: editIndex != null ? rows[editIndex].soId : null,
            soDtsID: editIndex != null ? rows[editIndex].soDtsID : null
        };

        const existingRowIndex = rows.findIndex(
            (r, i) => r.productCode === current.productCode && i !== editIndex
        );

        // if (existingRowIndex !== -1 && editIndex == null) {
        //     setRows(prev =>
        //         prev.map((r, i) => {
        //             if (i === existingRowIndex) {
        //                 const newQty = Number(r.qty) + qty;
        //                 const newTaxableAmt = newQty * rate * (1 - disc / 100);
        //                 const newTaxAmount = newTaxableAmt * (tax / 100);
        //                 return {
        //                     ...r,
        //                     qty: newQty,
        //                     rate,
        //                     disc,
        //                     tax,
        //                     remark: current.remark || r.remark,
        //                     TaxableAmt: newTaxableAmt.toFixed(2),
        //                     taxAmount: newTaxAmount.toFixed(2),
        //                     amount: newTaxableAmt.toFixed(2),
        //                     CGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
        //                     SGST: state?.toLowerCase() === compState?.toLowerCase() ? (tax / 2).toFixed(2) : '0.00',
        //                     IGST: state?.toLowerCase() !== compState?.toLowerCase() ? tax.toFixed(2) : '0.00',
        //                     CGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (newTaxAmount / 2).toFixed(2) : '0.00',
        //                     SGSTAmt: state?.toLowerCase() === compState?.toLowerCase() ? (newTaxAmount / 2).toFixed(2) : '0.00',
        //                     IGSTAmt: state?.toLowerCase() !== compState?.toLowerCase() ? newTaxAmount.toFixed(2) : '0.00',
        //                     UTGST: current.UTGST,
        //                     GSTCess: current.GSTCess,
        //                     TaxId: current.TaxId,
        //                     challanDtsID: r.challanDtsID,
        //                     challanID: r.challanID,
        //                     soId: r.soId,
        //                     soDtsID: r.soDtsID
        //                 };
        //             }
        //             return r;
        //         })
        //     );
        // } else {
        //     if (editIndex != null) {
        //         setRows(prev => prev.map((r, i) => (i === editIndex ? row : r)));
        //     } else {
        //         setRows(prev => [row, ...prev]);
        //         setNextSrNo(prev => prev + 1);
        //     }
        // }

        if (editIndex != null) {
            setRows(prev => prev.map((r, i) => i === editIndex ? { ...row, srNo: rows[editIndex].srNo } : r));
        } else {
            setRows(prev => [row, ...prev]);
            setNextSrNo(prev => prev + 1);
        }

        setIsEditingMode(false);
        setCurrent({ Id: '', productCode: '', description: '', UnitId: '', unit: '', qty: 1, rate: 0, disc: 0, tax: 0, remark: '', CGST: '0.00', SGST: '0.00', IGST: '0.00', UTGST: '0.00', GSTCess: '0.00', CGSTAmt: '0.00', SGSTAmt: '0.00', IGSTAmt: '0.00', TaxableAmt: '0.00', taxAmount: '', amount: '', TaxId: null, TransType: 100, challanDtsID: null, challanID: null, soId: null, soDtsID: null, refNo: '', refDate: '', refQty: '', type: '', size: '', color: '', freeQty: '', per: '', pkg: '', oth1: '', oth2: '', oth3: '', GSTOth: '0.00', poQty: '', ex4: '', ex5: '', exQty6: '', serialNo: '', oth4: '', oth5: '', oth6: '', oth7: '' });
        setErrors({});
        setEditIndex(null);
        setOriginalEditQty(null);

        // Focus on Product Code field after adding product (not after updating)
        if (editIndex == null) {
            setTimeout(() => {
                productCodeRef.current?.focus?.();
            }, 0);
        }
    };

    const editRow = idx => {

        if (isProductLocked) return;

        const row = rows[idx];
        const matchedUnit = unitOptions.find(u => String(u.UnitId) === String(row.UnitId)) ||
            unitOptions.find(u => String(u.UnitId) === String(row.unit)) ||
            unitOptions.find(u => u.unit?.toLowerCase() === String(row.unit).toLowerCase());

        // This row's Qty/Rate/Pkg/Disc are about to be loaded into `current` from
        // the DB values — skip the next live-calculation pass so it doesn't
        // immediately overwrite the stored amount before the user edits anything.
        skipNextCalcRef.current = true;

        setCurrent({
            ...row,
            Id: row.productId,
            UnitId: matchedUnit ? String(matchedUnit.UnitId) : row.UnitId || '',
            unit: matchedUnit ? matchedUnit.unit : row.unit || '',
            CGST: String(row.CGST || '0.00'),
            SGST: String(row.SGST || '0.00'),
            IGST: String(row.IGST || '0.00'),
            CGSTAmt: String(row.CGSTAmt || '0.00'),
            SGSTAmt: String(row.SGSTAmt || '0.00'),
            IGSTAmt: String(row.IGSTAmt || '0.00'),
            UTGST: String(row.UTGST || '0.00'),
            GSTCess: String(row.GSTCess || '0.00'),
            TaxableAmt: String(row.TaxableAmt || '0.00'),
            taxAmount: String(row.taxAmount || '0.00'),
            amount: String(row.amount || '0.00'),
            TaxId: row.TaxId || null,
            challanDtsID: row.challanDtsID || null,
            challanID: row.challanID || null,
            qoutID: row.qoutID || null,
            qoutDtsID: row.qoutDtsID || null,
            soId: row.soId || null,
            soDtsID: row.soDtsID || null
        });
        setEditIndex(idx);
        setOriginalEditQty(Number(row.qty) || 0);
        setIsEditingMode(true);
    };

    const deleteRow = idx => {

        if (isProductLocked) return;

        setRows(prev => prev.filter((_, i) => i !== idx));
        if (idx === editIndex) {
            setCurrent({ Id: '', productCode: '', description: '', UnitId: '', unit: '', qty: 1, rate: 0, disc: 0, tax: 0, remark: '', CGST: '0.00', SGST: '0.00', IGST: '0.00', UTGST: '0.00', GSTCess: '0.00', CGSTAmt: '0.00', SGSTAmt: '0.00', IGSTAmt: '0.00', TaxableAmt: '0.00', taxAmount: '', amount: '', TaxId: null, TransType: 100, challanDtsID: null, challanID: null, soId: null, soDtsID: null, refNo: '', refDate: '', refQty: '', type: '', size: '', color: '', freeQty: '', per: '', pkg: '', oth1: '', oth2: '', oth3: '', GSTOth: '0.00', poQty: '', ex4: '', ex5: '', exQty6: '', serialNo: '', oth4: '', oth5: '', oth6: '', oth7: '', oth1Amt: null, oth2Amt: null, oth3Amt: null, oth4Amt: null, oth5Amt: null, oth6Amt: null, oth7Amt: null });
            setEditIndex(null);
            setErrors({});
            setOriginalEditQty(null);
            setIsEditingMode(false);
        }
    };

    const handleNext = () => {
        if (rows.length === 0) {
            setErrors({ global: 'Add at least one product' });
            return;
        }

        const isSameState = state?.toLowerCase() === compState?.toLowerCase();

        const payloadRows = rows.map(r => {
            const qty = Number(r.qty) || 0;
            const rate = Number(r.rate) || 0;
            const gross = getAmountCalcSafe(qty, rate, r.pkg, 0, isApplyPkgInAmtEnabled, calculateAmount).gross;
            const taxable = Number(r.amount || r.TaxableAmt) || 0;
            // Disc% is against gross only — independent of any Oth1-7
            // adjustment baked into `taxable`, which is the final amount.
            const discAmt = gross * ((Number(r.disc) || 0) / 100);
            const taxPct = Number(r.tax) || 0;
            const taxAmt = Number(r.taxAmount) || (taxable * (taxPct / 100));

            const cgstAmt = (r.CGSTAmt != null && r.CGSTAmt !== '') ? Number(r.CGSTAmt) : (isSameState ? taxAmt / 2 : 0);
            const sgstAmt = (r.SGSTAmt != null && r.SGSTAmt !== '') ? Number(r.SGSTAmt) : (isSameState ? taxAmt / 2 : 0);
            const igstAmt = (r.IGSTAmt != null && r.IGSTAmt !== '') ? Number(r.IGSTAmt) : (isSameState ? 0 : taxAmt);

            const { taxId, ...rest } = r; //  remove small taxId
            return {
                ...rest,
                productId: r.productId,
                productCode: r.productCode,
                description: r.description,
                unit: r.unit,
                UnitId: Number(r.UnitId) || null,
                qty,
                rate,
                disc: Number(r.disc) || 0,
                tax: taxPct,
                batchNo: r.batchNo || '',
                expDate: r.expDate || null,
                serialNo: r.serialNo || '',
                remark: r.remark || '',
                gross: Number(gross.toFixed(2)),
                amount: Number(taxable.toFixed(2)),
                DiscAmt: Number(discAmt.toFixed(2)),
                taxAmount: Number(taxAmt.toFixed(2)),
                srNo: r.srNo,
                TransType: r.TransType || 100,
                TaxId: r.TaxId || null,
                CGST: isSameState ? Number((taxPct / 2).toFixed(2)) : 0,
                SGST: isSameState ? Number((taxPct / 2).toFixed(2)) : 0,
                IGST: isSameState ? 0 : Number(taxPct.toFixed(2)),
                CGSTAmt: Number(cgstAmt.toFixed(2)),
                SGSTAmt: Number(sgstAmt.toFixed(2)),
                IGSTAmt: Number(igstAmt.toFixed(2)),
                UTGST: r.UTGST || '0.00',
                GSTCess: r.GSTCess || '0.00',
                refNo: r.refNo || null,
                refDate: r.refDate || null,
                refQty: r.refQty || null,
                type: r.type || null,
                size: r.size || null,
                color: r.color || null,
                freeQty: r.freeQty || null,
                per: r.per || null,
                pkg: r.pkg || null,
                oth1: r.oth1 || null,
                oth1Amt: r.oth1Amt || null,
                oth2: r.oth2 || null,
                oth2Amt: r.oth2Amt || null,
                oth3: r.oth3 || null,
                oth3Amt: r.oth3Amt || null,
                oth4: r.oth4 || null,
                oth4Amt: r.oth4Amt || null,
                oth5: r.oth5 || null,
                oth5Amt: r.oth5Amt || null,
                oth6: r.oth6 || null,
                oth6Amt: r.oth6Amt || null,
                oth7: r.oth7 || null,
                oth7Amt: r.oth7Amt || null,
                GSTOth: r.GSTOth || null,
                challanDtsID: r.challanDtsID || null,
                challanID: r.challanID || null,
                qoutID: r.qoutID || null,
                qoutDtsID: r.qoutDtsID || null,
                soId: r.soId || null,
                soDtsID: r.soDtsID || null
            };
        });

        // Additional Charges: charges saved in UserDefineColumns with ColToBeAsk=1
        // (i.e. present in `additionalCharges`, sourced from UserDefineContext) are
        // folded into the invoice payload — Adc{SrNo}Amt/Adc{SrNo}TaxId in summary
        // for all 5 fixed slots, plus one pseudo item row per SELECTED charge
        // (TransType 200-204, SrNo 20000-20004) appended to tableData.
        // When a charge's Tax is disabled, its TaxId is resolved to the taxmaster
        // row where GST = 0 (found via gsts, whose `value` doubles as the % — same
        // pattern already used elsewhere in this file, e.g. parseFloat(opt.value)).
        const zeroTaxId = gsts.find(g => parseFloat(g.value) === 0)?.Id ?? null;

        const additionalChargeSummary = {};
        const additionalChargeItems = [];
        for (let slot = 1; slot <= 5; slot++) {
            const charge = additionalCharges.find(c => Number(c.SrNo) === slot);
            if (!charge) {
                additionalChargeSummary[`Adc${slot}Amt`] = 0;
                additionalChargeSummary[`Adc${slot}TaxId`] = null;
                continue;
            }
            const key = charge.TransType;
            const input = chargeInputs[key] || { amount: '', taxId: charge.TaxId ?? null };
            const { taxPercent, taxAmount, finalAmount } = getChargeFinalAmounts(charge, input);
            const amount = parseFloat(input.amount) || 0;
            const taxId = charge.TaxEnabled ? (input.taxId ?? null) : zeroTaxId;

            additionalChargeSummary[`Adc${slot}Amt`] = amount;
            additionalChargeSummary[`Adc${slot}TaxId`] = taxId;

            // The item row is shaped exactly like a normal product row (same field
            // set as payloadRows above) instead of a small subset — CGST/SGST/IGST
            // hold the % rate, CGSTAmt/SGSTAmt/IGSTAmt hold the tax amounts,
            // TaxableAmt holds the tax-inclusive final amount (e.g. 100 + 18% tax =
            // 118), and every other normal-row field gets a sensible default/analog
            // for a charge line (no productCode/qty/rate in the usual sense, so
            // ChargeName stands in for productCode/description, qty=1, rate=amount
            // so gross/amount line up the same way a real row's do).
            const chargeItemId = getChargeItemId('PROFORMA', slot);
            additionalChargeItems.push({
                Id: chargeItemId,
                productId: chargeItemId,
                productCode: charge.ChargeName || '',
                description: charge.ChargeName || '',
                UnitId: null,
                unit: '',
                qty: 1,
                rate: amount,
                disc: 0,
                tax: Number(taxPercent.toFixed(2)),
                batchNo: '',
                expDate: null,
                serialNo: '',
                amount: amount,
                taxAmount: Number(taxAmount.toFixed(2)),
                remark: '',
                TaxId: taxId,
                CGST: isSameState ? Number((taxPercent / 2).toFixed(2)) : 0,
                SGST: isSameState ? Number((taxPercent / 2).toFixed(2)) : 0,
                IGST: isSameState ? 0 : Number(taxPercent.toFixed(2)),
                UTGST: '0.00',
                GSTCess: '0.00',
                GSTOth: '0.00',
                GSTEduCess: '0.00',
                GSTEducessAmt: '0.00',
                CGSTAmt: isSameState ? Number((taxAmount / 2).toFixed(2)) : 0,
                SGSTAmt: isSameState ? Number((taxAmount / 2).toFixed(2)) : 0,
                IGSTAmt: isSameState ? 0 : Number(taxAmount.toFixed(2)),
                GSTCEssAmt: '0.00',
                TaxableAmt: Number(finalAmount.toFixed(2)),
                challanDtsID: null,
                challanID: null,
                qoutID: null,
                qoutDtsID: null,
                soId: null,
                soDtsID: null,
                PONo: null,
                PODate: null,
                poQty: null,
                type: null,
                size: null,
                color: null,
                freeQty: null,
                per: null,
                pkg: null,
                oth1: null,
                oth2: null,
                oth3: null,
                oth4: null,
                oth5: null,
                oth6: null,
                oth7: null,
                ex4: null,
                ex5: null,
                exQty6: null,
                serialNo: '',
                configData: [],
                _isNew: true,
                configType: null,
                applyBatchNo: 0,
                applySerialNo: 0,
                warrantyMonths: null,
                expiryDays: null,
                srNo: 19999 + slot,
                dispQty: 0,
                gross: amount,
                DiscAmt: 0,
                refNo: null,
                refDate: null,
                refQty: null,
                oth1Amt: null,
                oth2Amt: null,
                oth3Amt: null,
                oth4Amt: null,
                oth5Amt: null,
                oth6Amt: null,
                oth7Amt: null,
                // Fixed identifiers driving the backend's TransType-aware SrNo
                // override (see dispatchController.js / proformaInvoiceController.js).
                SrNo: 19999 + slot,
                TransType: 199 + slot,
            });
        }

        const payload = {
            tableData: [...payloadRows, ...additionalChargeItems],
            summary: {
                basic: Number(totals.basic),
                grossBasic: Number(totals.grossBasic),
                discount: Number(totals.discount),
                cgst: Number(totals.cgst) + Number(chargesSummary.cgst || 0),
                sgst: Number(totals.sgst) + Number(chargesSummary.sgst || 0),
                igst: Number(totals.igst) + Number(chargesSummary.igst || 0),
                roundOff: Number(totals.roundOff),
                total: Number(totals.total),
                ...additionalChargeSummary
            },
            chargeInputs: chargeInputs
        };

        onNext(payload);
    };

    const formatINR = val => {
        const num = Number(val) || 0;
        return num.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const columns = [
        { field: 'srNo', headerName: '#', width: 38, align: 'center', headerAlign: 'center' },
        {
            field: 'product', headerName: 'Product', flex: 1, sortable: false, filterable: false,
            renderCell: params => {
                const { productCode, description, unit, tax, batchNo, expDate, serialNo, remark, CGSTAmt, SGSTAmt, IGSTAmt, SONumber, SODate } = params.row;
                const isSameState = state?.toLowerCase() === compState?.toLowerCase();
                const matchedGST = gsts.find(g => String(g.Id) === String(params.row.TaxId));
                const displayTax = matchedGST
                    ? (matchedGST.value?.toLowerCase().includes('tax free') ? '0%' : matchedGST.value)
                    : (!params.row.tax || Number(params.row.tax) === 0
                        ? '0%'
                        : `${params.row.tax}%`);
                return (
                    <Box sx={{ py: 0.5, display: 'flex', flexDirection: 'column' }}>
                        <Box>
                            <Typography variant="body2" fontWeight="bold" component="div"
                                sx={{
                                    cursor: isProductLocked ? 'not-allowed' : 'pointer',
                                    color: isProductLocked ? themeColors.neutral.muted : colors.primary.main,
                                    textDecoration: isProductLocked ? 'none' : 'underline',
                                    fontSize: '0.85rem',
                                }}
                                onClick={e => {
                                    e.stopPropagation();
                                    if (isProductLocked) return;
                                    editRow(params.row.index);
                                }}
                            >
                                {productCode}
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.85rem' }}>
                                {description}
                            </Typography>
                        </Box>
                        {(isColVisible(COL.UNIT) || isColVisible(COL.TAX_CODE)) && (
                            <Typography variant="caption" display="block">
                                {isColVisible(COL.UNIT) && `Unit: ${unit || 'N/A'}`}
                                {isColVisible(COL.UNIT) && isColVisible(COL.TAX_CODE) && ' | '}
                                {isColVisible(COL.TAX_CODE) && `GST: ${displayTax}`}
                            </Typography>
                        )}
                        {(isSameState ? (isColVisible('SGST Rs.') || isColVisible('CGST Rs.')) : isColVisible('IGST Rs.')) && (
                            <Typography variant="caption" display="block" color="text.secondary">
                                {isSameState
                                    ? [
                                        isColVisible('SGST Rs.') && `SGST: ₹${formatINR(SGSTAmt)}`,
                                        isColVisible('CGST Rs.') && `CGST: ₹${formatINR(CGSTAmt)}`,
                                    ].filter(Boolean).join(' + ')
                                    : `IGST: ₹${formatINR(IGSTAmt)}`}
                            </Typography>
                        )}
                        {((batchNo && isColVisible(COL.BATCH_NO)) || (expDate && isColVisible(COL.EXP_DATE)) || serialNo) && (
                            <Typography variant="caption" display="block">
                                {batchNo && isColVisible(COL.BATCH_NO) && `Batch: ${batchNo}`}
                                {expDate && isColVisible(COL.EXP_DATE) && ` | Exp: ${expDate}`}
                            </Typography>
                        )}
                        {remark && isColVisible(COL.REMARK) && (<Typography variant="caption" display="block" color="text.secondary"> Remark: {remark} </Typography>)}
                        {OTH_FIELDS.some(({ field, col }) => isColVisible(col) && params.row[othDisplayField(field, col)]) && (
                            <Typography variant="caption" display="block" color="text.secondary">
                                {OTH_FIELDS
                                    .filter(({ field, col }) => isColVisible(col) && params.row[othDisplayField(field, col)])
                                    .map(({ field, col }) => {
                                        const rupees = isOthRupeesMode(col);
                                        const displayVal = params.row[othDisplayField(field, col)];
                                        const amt = params.row[`${field}Amt`];
                                        const sign = Boolean(getColRow(col)?.AddLess) ? '-' : '';
                                        return rupees
                                            ? `${getColLabel(col, field)}: ${sign}₹${formatINR(displayVal)}`
                                            : `${getColLabel(col, field)}: ${displayVal}%${amt ? ` (${sign}₹${formatINR(amt)})` : ''}`;
                                    })
                                    .join(' | ')}
                            </Typography>
                        )}
                        {(SONumber || SODate) && (
                            <Typography variant="caption" display="block" color='blue'>
                                {SONumber && `${(params.row.soId == null && params.row.soDtsID == null && params.row.qoutDtsID != null) ? 'QuotNo' : 'OrderNo'}: ${SONumber}`}
                                {SODate && ` | Date: ${SODate}`}
                            </Typography>
                        )}
                    </Box>
                );
            }
        },
        { field: 'qty', headerName: 'Qty', width: 50, align: 'center', headerAlign: 'center' },
        { field: 'rate', headerName: 'Rate', width: 100, align: 'right', headerAlign: 'center', renderCell: params => `₹${formatINR(params.row.rate)}` },
        { field: 'disc', headerName: 'Disc (%)', width: 50, align: 'center', headerAlign: 'center' },
        { field: 'amount', headerName: 'Amount ₹', width: 100, align: 'right', headerAlign: 'right', renderCell: params => `₹${formatINR(Number(params.row.TaxableAmt || 0))}` },
        {
            width: 30, sortable: false, filterable: false, align: 'center', headerAlign: 'center',
            renderCell: params => (
                <IconButton size="small" onClick={() => deleteRow(params.row.index)} color="error" disabled={isProductLocked}>
                    <DeleteIcon fontSize="small" />
                </IconButton>
            )
        }
    ];

    return (
        <Paper sx={{ p: 1, overflowX: 'hidden', mt: -1.5 }}>
            <Box display="flex" flexDirection={isSmall ? 'column' : 'row'} gap={1.5} sx={{ width: '100%', boxSizing: 'border-box', flexWrap: isSmall ? 'wrap' : 'nowrap', overflow: 'hidden' }} >
                <Box display="flex" flexDirection="column" gap={2} sx={{ width: '100%', maxWidth: isSmall ? '100%' : 420, boxSizing: 'border-box', p: 2, borderRight: isSmall ? 'none' : `1px solid ${themeColors.neutral.line}`, flex: { lg: '1 1 100%' }, }} >
                    <Box>
                        <DropdownProductCode ref={productCodeRef} id="productCode" options={allProducts} value={current.productCode} onSelect={handleSelect} onNewProductAdded={loadAllProducts} connectionString={connectionString} searchBy="code" onProductMasterClose={handleProductAdded} autoFocus={!initialData?.tableData?.length} />
                        {!!errors.productCode && (<Typography color="error" variant="caption"> {errors.productCode} </Typography>)}
                    </Box>
                    {/* <TextField label="Description" required fullWidth multiline minRows={1} size="small" placeholder="Enter product description" value={current.description} onChange={handleChange('description')} InputLabelProps={{ sx: requiredAsteriskStyle }} sx={{ '& .MuiInputBase-root:not(.MuiInputBase-multiline)': { height: 38 }, '& .MuiOutlinedInput-inputMultiline': { padding: '8px 12px', minHeight: '80px' } }} /> */}
                    {isColVisible(COL.DESCRIPTION) && (
                        <DropdownProductCode
                            id="description"
                            label="Description"
                            options={allProducts}
                            value={current.description}
                            onSelect={handleSelect}
                            onNewProductAdded={loadAllProducts}
                            connectionString={connectionString}
                            searchBy="description"   // ✅ IMPORTANT
                            onProductMasterClose={handleProductAdded}
                        />
                    )}
                    {/* {(isColVisible('Ref.No') || isColVisible('Ref. Date') || isColVisible('Ref. Qty')) && (
                        <Box display="flex" flexDirection="row" gap={1.5}>
                            {isColVisible('Ref.No') && (
                                <TextField label="Ref. No" size="small" fullWidth value={current.refNo} onChange={handleChange('refNo')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible('Ref. Date') && (
                                <TextField label="Ref. Date" type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={current.refDate} onChange={handleChange('refDate')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible('Ref. Qty') && (
                                <TextField label="Ref. Qty" type="number" size="small" fullWidth value={current.refQty} onChange={handleChange('refQty')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                        </Box>
                    )} */}
                    {(isColVisible(COL.TYPE) || isColVisible(COL.SIZE) || isColVisible(COL.COLOR)) && (
                        <Box display="flex" flexDirection="row" gap={1.5}>
                            {isColVisible(COL.TYPE) && (
                                <TextField label={getColLabel(COL.TYPE, 'Type')} size="small" fullWidth value={current.type} onChange={handleChange('type')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.SIZE) && (
                                <TextField label={getColLabel(COL.SIZE, 'Size')} size="small" fullWidth value={current.size} onChange={handleChange('size')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.COLOR) && (
                                <TextField label={getColLabel(COL.COLOR, 'Color')} size="small" fullWidth value={current.color} onChange={handleChange('color')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                        </Box>
                    )}
                    <Box display="flex" flexDirection="row" gap={1.5}>
                        {isColVisible(COL.UNIT) && (
                            <TextField label="Unit" select required fullWidth size='small' value={String(current.UnitId)} onChange={handleChange('UnitId')} SelectProps={{ native: true }} InputLabelProps={{ sx: requiredAsteriskStyle }} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} >
                                <option value=""></option>
                                {unitOptions.map(u => (
                                    <option key={u.UnitId} value={u.UnitId}>
                                        {u.unit}
                                    </option>
                                ))}
                            </TextField>
                        )}
                        {/* <TextField label="Tax(%)" fullWidth type="number" disabled value={current.tax} onChange={handleChange('tax')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} /> */}
                        {isColVisible(COL.TAX_CODE) && (
                            <Dropdown
                                id="gst"
                                label="GST"
                                valueKey="Id"
                                labelKey="value"
                                value={current.TaxId ? String(current.TaxId) : ""}
                                options={gsts}
                                required
                                onChange={(opt) => {
                                    if (!opt) {
                                        setCurrent(prev => ({
                                            ...prev,
                                            tax: 0,
                                            TaxId: null
                                        }));
                                    } else {
                                        const gstValue = parseFloat(opt.value) || 0;

                                        setCurrent(prev => ({
                                            ...prev,
                                            tax: gstValue,   // % value used in calculation
                                            TaxId: opt.Id    // backend id
                                        }));
                                    }
                                }}
                                error={!!errors.TaxId}
                            />
                        )}
                    </Box>
                    {(isColVisible(COL.BATCH_NO) || isColVisible(COL.EXP_DATE)) && (
                        <Box display="flex" flexDirection="row" gap={1.5}>
                            {isColVisible(COL.BATCH_NO) && (
                                <TextField label={getColLabel(COL.BATCH_NO, 'Batch No')} size="small" fullWidth value={current.batchNo} onChange={handleChange('batchNo')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.EXP_DATE) && (
                                <TextField label={getColLabel(COL.EXP_DATE, 'Exp. Date')} type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={current.expDate} onChange={handleChange('expDate')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                        </Box>
                    )}
                    <Box display="flex" flexDirection="row" gap={1.5}>
                        {isColVisible(COL.QTY) && (
                            <TextField label="Qty" required size='small' fullWidth type="number" inputProps={{ min: 0, step: 'any' }} value={current.qty} onChange={handleChange('qty')} error={!!errors.qty} helperText={errors.qty} InputLabelProps={{ sx: requiredAsteriskStyle }} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                        )}
                        {isColVisible(COL.RATE) && (
                            <TextField label="Rate" required size='small' fullWidth type="number" inputProps={{ min: 0 }} value={current.rate} onChange={handleChange('rate')} InputLabelProps={{ sx: requiredAsteriskStyle }} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                        )}
                        {isColVisible(COL.DISC) && (
                            <TextField label={getColLabel(COL.DISC, 'Disc%')} size='small' fullWidth type="number" inputProps={{ min: 0, max: 100 }} value={current.disc} onChange={handleBaseFieldChange('disc')} InputLabelProps={{ sx: requiredAsteriskStyle }} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                        )}
                    </Box>
                    {(isColVisible(COL.PKG) || isColVisible(COL.FREE_QTY) || isColVisible(COL.PER)) && (
                        <Box display="flex" flexDirection="row" gap={1.5}>
                            {isColVisible(COL.PKG) && (
                                <TextField label={getColLabel(COL.PKG, 'Pkg')} size="small" fullWidth value={current.pkg || ''} onChange={handleChange('pkg')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.FREE_QTY) && (
                                <TextField label={getColLabel(COL.FREE_QTY, 'Free Qty')} type="number" size="small" fullWidth value={current.freeQty} onChange={handleChange('freeQty')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.PER) && (
                                <TextField label={getColLabel(COL.PER, 'Per')} size="small" fullWidth value={current.per} onChange={handleChange('per')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                        </Box>
                    )}
                    {(isColVisible(COL.OTH1) || isColVisible(COL.OTH2) || isColVisible(COL.OTH3)) && (
                        <Box display="flex" flexDirection="row" flexWrap="wrap" gap={1.5}>
                            {isColVisible(COL.OTH1) && (
                                <TextField label={getColLabel(COL.OTH1, 'Oth 1')} size="small" fullWidth type="number" value={current[othDisplayField('oth1', COL.OTH1)] || ''} onChange={handleOthChange('oth1', COL.OTH1)} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.OTH1) && !isOthRupeesMode(COL.OTH1) && (
                                <TextField label={`${getColLabel(COL.OTH1, 'Oth 1')} Amount`} size="small" fullWidth disabled value={current.oth1Amt || ''} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.OTH2) && (
                                <TextField label={getColLabel(COL.OTH2, 'Oth 2')} size="small" fullWidth type="number" value={current[othDisplayField('oth2', COL.OTH2)] || ''} onChange={handleOthChange('oth2', COL.OTH2)} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.OTH2) && !isOthRupeesMode(COL.OTH2) && (
                                <TextField label={`${getColLabel(COL.OTH2, 'Oth 2')} Amount`} size="small" fullWidth disabled value={current.oth2Amt || ''} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.OTH3) && (
                                <TextField label={getColLabel(COL.OTH3, 'Oth 3')} size="small" fullWidth type="number" value={current[othDisplayField('oth3', COL.OTH3)] || ''} onChange={handleOthChange('oth3', COL.OTH3)} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.OTH3) && !isOthRupeesMode(COL.OTH3) && (
                                <TextField label={`${getColLabel(COL.OTH3, 'Oth 3')} Amount`} size="small" fullWidth disabled value={current.oth3Amt || ''} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                        </Box>
                    )}
                    {(isColVisible(COL.UTGST) || isColVisible(COL.GST_CESS) || isColVisible(COL.GST_OTH)) && (
                        <Box display="flex" flexDirection="row" gap={1.5}>
                            {isColVisible(COL.UTGST) && (
                                <TextField label={getColLabel(COL.UTGST, 'UTGST')} size="small" fullWidth type="number" value={current.UTGST} onChange={handleChange('UTGST')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.GST_CESS) && (
                                <TextField label={getColLabel(COL.GST_CESS, 'GST Cess')} size="small" fullWidth type="number" value={current.GSTCess} onChange={handleChange('GSTCess')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.GST_OTH) && (
                                <TextField label={getColLabel(COL.GST_OTH, 'GST Oth')} size="small" fullWidth type="number" value={current.GSTOth} onChange={handleChange('GSTOth')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                        </Box>
                    )}
                    {(isColVisible(COL.PO_NO) || isColVisible(COL.PO_DATE)) && (
                        <Box display="flex" flexDirection="row" gap={1.5}>
                            {isColVisible(COL.PO_NO) && (
                                <TextField label={getColLabel(COL.PO_NO, 'PO No')} size="small" fullWidth value={current.PONo} onChange={handleChange('PONo')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                            {isColVisible(COL.PO_DATE) && (
                                <TextField label={getColLabel(COL.PO_DATE, 'PO Date')} type="date" size="small" fullWidth InputLabelProps={{ shrink: true }} value={current.PODate} onChange={handleChange('PODate')} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                            )}
                        </Box>
                    )}
                    {/* PO Qty / EX4 */}
                    {(isColVisible(COL.PO_QTY) || isColVisible(COL.EX4)) && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                            {isColVisible(COL.PO_QTY) && (
                                <TextField
                                    size="small"
                                    label={getColLabel(COL.PO_QTY, 'PO Qty')}
                                    type="number"
                                    value={current.poQty || ''}
                                    onChange={(e) => setCurrent(p => ({ ...p, poQty: e.target.value }))}
                                    disabled={isProductLocked}
                                />
                            )}
                            {isColVisible(COL.EX4) && (
                                <TextField
                                    size="small"
                                    label={getColLabel(COL.EX4, 'EX4')}
                                    value={current.ex4 || ''}
                                    onChange={(e) => setCurrent(p => ({ ...p, ex4: e.target.value }))}
                                    disabled={isProductLocked}
                                />
                            )}
                        </Box>
                    )}
                    {/* EX5 / EXQty6 */}
                    {(isColVisible(COL.EX5) || isColVisible(COL.EX_QTY6)) && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
                            {isColVisible(COL.EX5) && (
                                <TextField
                                    size="small"
                                    label={getColLabel(COL.EX5, 'EX5')}
                                    value={current.ex5 || ''}
                                    onChange={(e) => setCurrent(p => ({ ...p, ex5: e.target.value }))}
                                    disabled={isProductLocked}
                                />
                            )}
                            {isColVisible(COL.EX_QTY6) && (
                                <TextField
                                    size="small"
                                    label={getColLabel(COL.EX_QTY6, 'EXQty6')}
                                    type="number"
                                    value={current.exQty6 || ''}
                                    onChange={(e) => setCurrent(p => ({ ...p, exQty6: e.target.value }))}
                                    disabled={isProductLocked}
                                />
                            )}
                        </Box>
                    )}
                    {/* Grade */}
                    {isColVisible(COL.SERIAL_NO) && (
                        <TextField
                            size="small"
                            label={getColLabel(COL.SERIAL_NO, 'Serial No')}
                            value={current.serialNo || ''}
                            onChange={(e) => setCurrent(p => ({ ...p, serialNo: e.target.value }))}
                            disabled={isProductLocked}
                            fullWidth
                        />
                    )}
                    {/* Oth 4 / Oth 5 */}
                    {(isColVisible(COL.OTH4) || isColVisible(COL.OTH5)) && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(0, 1fr))', gap: 2, mb: 2 }}>
                            {isColVisible(COL.OTH4) && (
                                <TextField
                                    size="small"
                                    type="number"
                                    label={getColLabel(COL.OTH4, 'Oth 4')}
                                    value={current[othDisplayField('oth4', COL.OTH4)] || ''}
                                    onChange={handleOthChange('oth4', COL.OTH4)}
                                    disabled={isProductLocked}
                                />
                            )}
                            {isColVisible(COL.OTH4) && !isOthRupeesMode(COL.OTH4) && (
                                <TextField size="small" disabled label={`${getColLabel(COL.OTH4, 'Oth 4')} Amount`} value={current.oth4Amt || ''} />
                            )}
                            {isColVisible(COL.OTH5) && (
                                <TextField
                                    size="small"
                                    type="number"
                                    label={getColLabel(COL.OTH5, 'Oth 5')}
                                    value={current[othDisplayField('oth5', COL.OTH5)] || ''}
                                    onChange={handleOthChange('oth5', COL.OTH5)}
                                    disabled={isProductLocked}
                                />
                            )}
                            {isColVisible(COL.OTH5) && !isOthRupeesMode(COL.OTH5) && (
                                <TextField size="small" disabled label={`${getColLabel(COL.OTH5, 'Oth 5')} Amount`} value={current.oth5Amt || ''} />
                            )}
                        </Box>
                    )}
                    {/* Oth 6 / Oth 7 */}
                    {(isColVisible(COL.OTH6) || isColVisible(COL.OTH7)) && (
                        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(0, 1fr))', gap: 2, mb: 2 }}>
                            {isColVisible(COL.OTH6) && (
                                <TextField
                                    size="small"
                                    type="number"
                                    label={getColLabel(COL.OTH6, 'Oth 6')}
                                    value={current[othDisplayField('oth6', COL.OTH6)] || ''}
                                    onChange={handleOthChange('oth6', COL.OTH6)}
                                    disabled={isProductLocked}
                                />
                            )}
                            {isColVisible(COL.OTH6) && !isOthRupeesMode(COL.OTH6) && (
                                <TextField size="small" disabled label={`${getColLabel(COL.OTH6, 'Oth 6')} Amount`} value={current.oth6Amt || ''} />
                            )}
                            {isColVisible(COL.OTH7) && (
                                <TextField
                                    size="small"
                                    type="number"
                                    label={getColLabel(COL.OTH7, 'Oth 7')}
                                    value={current[othDisplayField('oth7', COL.OTH7)] || ''}
                                    onChange={handleOthChange('oth7', COL.OTH7)}
                                    disabled={isProductLocked}
                                />
                            )}
                            {isColVisible(COL.OTH7) && !isOthRupeesMode(COL.OTH7) && (
                                <TextField size="small" disabled label={`${getColLabel(COL.OTH7, 'Oth 7')} Amount`} value={current.oth7Amt || ''} />
                            )}
                        </Box>
                    )}
                    {isColVisible(COL.AMOUNT) && (
                        <TextField label="Amount" fullWidth disabled type="number" size="small" value={current.amount} sx={{ '& .MuiOutlinedInput-root': { height: 38, alignItems: 'center' } }} />
                    )}
                    {isColVisible(COL.REMARK) && (
                        <Box>
                            <TextField label="Remark" fullWidth multiline minRows={1} maxRows={3} size="small" value={current.remark || ''} onChange={handleChange('remark')} inputProps={{ maxLength: 250 }} helperText={`${(current.remark || '').length}/250 characters`} />
                        </Box>
                    )}
                    <Box display="flex" justifyContent="center">
                        <Button variant="outlined" onClick={addProduct} sx={{ minWidth: 180 }} disabled={isProductLocked}>
                            {editIndex != null ? 'Update Product' : 'Add Product'}
                        </Button>
                    </Box>
                </Box>
                <Box sx={{ width: isSmall ? '100%' : 670, maxWidth: '100%', p: isSmall ? 0 : 0, pl: 2, marginTop: isMobile ? -1 : -4, flexShrink: 0 }}>
                    {isMobile ? (
                        <Box>
                            {rows.filter(r => r.TransType !== 200 && r.TransType !== 201 && r.TransType !== 202 && r.TransType !== 203 && r.TransType !== 204 && r.TransType !== 300).map((row, i) => (
                                <Box
                                    key={i}
                                    sx={{
                                        border: `1px solid ${themeColors.neutral.lineStrong}`,
                                        borderRadius: 2,
                                        p: 1.5,
                                        mb: 1.5,
                                        color: isProductLocked && "text.disabled",
                                        cursor: isProductLocked ? "not-allowed" : "pointer",
                                    }}
                                >
                                    <Typography sx={{ fontWeight: 600 }}>
                                        {row.productCode} —{" "}
                                        <Box
                                            component="span"
                                            sx={{
                                                fontStyle: "italic",
                                                fontWeight: "normal",
                                                display: "inline",
                                                wordBreak: "break-word",
                                                whiteSpace: "normal"
                                            }}
                                        >
                                            {row.description}
                                        </Box>
                                    </Typography>

                                    {(row.SONumber || row.SODate) && (
                                        <Typography variant="caption" display="block" color='blue'>
                                            {row.SONumber && `${(row.soId == null && row.soDtsID == null && row.qoutDtsID != null) ? 'QuotNo' : 'OrderNo'}: ${row.SONumber}`}
                                            {row.SODate && ` | Date: ${row.SODate}`}
                                        </Typography>
                                    )}

                                    <Grid container justifyContent="space-between">
                                        <Typography>Qty:</Typography>
                                        <Typography>{row.qty}</Typography>
                                    </Grid>

                                    <Grid container justifyContent="space-between">
                                        <Typography>Rate:</Typography>
                                        <Typography>₹{row.rate}</Typography>
                                    </Grid>

                                    <Grid container justifyContent="space-between">
                                        <Typography>Disc:</Typography>
                                        <Typography>{row.disc}%</Typography>
                                    </Grid>

                                    <Grid container justifyContent="space-between">
                                        <Typography>Amount:</Typography>
                                        <Typography>₹{row.amount}</Typography>
                                    </Grid>

                                    {/* TAX INFO */}
                                    <Grid container justifyContent="space-between">
                                        <Typography>GST:</Typography>
                                        <Typography>{row.tax}%</Typography>
                                    </Grid>

                                    {state?.toLowerCase() === compState?.toLowerCase() ? (
                                        <>
                                            <Grid container justifyContent="space-between">
                                                <Typography>CGST:</Typography>
                                                <Typography>₹{formatINR(row.CGSTAmt)}</Typography>
                                            </Grid>
                                            <Grid container justifyContent="space-between">
                                                <Typography>SGST:</Typography>
                                                <Typography>₹{formatINR(row.SGSTAmt)}</Typography>
                                            </Grid>
                                        </>
                                    ) : (
                                        <Grid container justifyContent="space-between">
                                            <Typography>IGST:</Typography>
                                            <Typography>₹{formatINR(row.IGSTAmt)}</Typography>
                                        </Grid>
                                    )}

                                    {/* Remark */}
                                    {row.remark && row.remark.trim() !== '' && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                mt: 1,
                                                display: 'block',
                                                wordBreak: 'break-word',
                                                overflowWrap: 'anywhere',
                                                whiteSpace: 'normal'
                                            }}
                                        >
                                            Remark: {row.remark}
                                        </Typography>
                                    )}

                                    {/* DELETE / EDIT BUTTONS IF NEEDED */}
                                    <Box display="flex" justifyContent="flex-end" mt={1} gap={0.5}>
                                        <IconButton size="small" onClick={() => editRow(i)} disabled={isProductLocked}>
                                            <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => deleteRow(i)} disabled={isProductLocked}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    ) : (
                        <DataGrid
                            rows={rows.filter(r => r.TransType !== 200 && r.TransType !== 201 && r.TransType !== 202 && r.TransType !== 203 && r.TransType !== 204 && r.TransType !== 300).map((r, i) => ({ ...r, id: i + 1, index: i }))}
                            columns={columns}
                            getRowHeight={() => 'auto'}
                            autoHeight
                            disableRowSelectionOnClick
                            hideFooter
                            sx={{
                                mt: 5,
                                border: `1px solid ${themeColors.neutral.lineStrong}`,
                                borderRadius: 1,
                                '& .MuiDataGrid-columnHeaders': { minHeight: 55, maxHeight: 55 },
                                '& .MuiDataGrid-columnHeader': { backgroundColor: themeColors.neutral.gridHeader, color: themeColors.neutral.ink, fontWeight: 700, fontSize: '0.74rem', textTransform: 'uppercase', letterSpacing: '0.06em' },
                                '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: themeColors.neutral.surfaceAlt },
                                '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: themeColors.neutral.surface }
                            }}
                        />
                    )}
                    <Box mt={3}>
                        <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography>Basic Amount</Typography>
                            <Typography>₹{formatINR(totals.basic)}</Typography>
                        </Grid>
                        <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                            Total Discounted Amount: ₹{formatINR(totals.discount)}
                        </Typography>

                        {additionalCharges.length > 0 && (
                            <Box mb={2}>
                                <Accordion
                                    expanded={chargesCollapsibleOpen !== false ? true : chargesCollapsibleOpen}
                                    onChange={() => setChargesCollapsibleOpen(!chargesCollapsibleOpen)}
                                    sx={{ width: '100%' }}
                                >
                                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ py: 0.75, minHeight: 'auto' }}>
                                        <Typography variant="subtitle2">Charges</Typography>
                                    </AccordionSummary>
                                    <AccordionDetails sx={{ py: 1, px: 1 }}>
                                        <Box sx={{ width: '100%', overflowX: 'auto' }}>
                                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                                <colgroup>
                                                    <col style={{ width: '45%' }} />
                                                    <col style={{ width: '18%' }} />
                                                    <col style={{ width: '18%' }} />
                                                    <col style={{ width: '19%' }} />
                                                </colgroup>
                                                <thead>
                                                    <tr style={{ backgroundColor: themeColors.neutral.surfaceMuted, borderBottom: `2px solid ${themeColors.neutral.line}` }}>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left', borderRight: `1px solid ${themeColors.neutral.line}`, fontWeight: 600, fontSize: '12px' }}>Charge Name</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left', borderRight: `1px solid ${themeColors.neutral.line}`, fontWeight: 600, fontSize: '12px' }}>Amount</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left', borderRight: `1px solid ${themeColors.neutral.line}`, fontWeight: 600, fontSize: '12px' }}>Tax</th>
                                                        <th style={{ padding: '6px 8px', textAlign: 'left', fontWeight: 600, fontSize: '12px' }}>Final Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {additionalCharges.map(charge => {
                                                        const key = charge.TransType;
                                                        if (!chargeInputs[key]) {
                                                            updateChargeInput(key, { amount: '', taxId: charge.TaxId ?? null });
                                                        }
                                                        const input = chargeInputs[key] || { amount: '', taxId: charge.TaxId ?? null };
                                                        const { finalAmount } = getChargeFinalAmounts(charge, input);

                                                        return (
                                                            <tr key={key} style={{ borderBottom: `1px solid ${themeColors.neutral.line}` }}>
                                                                <td style={{ padding: '6px 8px', borderRight: `1px solid ${themeColors.neutral.line}`, verticalAlign: 'middle' }}>
                                                                    <Typography variant="caption" sx={{ fontSize: '12px' }}>{charge.ChargeName}</Typography>
                                                                </td>
                                                                <td style={{ padding: '6px 8px', borderRight: `1px solid ${themeColors.neutral.line}`, verticalAlign: 'middle' }}>
                                                                    <TextField
                                                                        size="small"
                                                                        fullWidth
                                                                        type="number"
                                                                        value={input.amount}
                                                                        onChange={(e) => updateChargeInput(key, { amount: e.target.value })}
                                                                        sx={{ '& .MuiOutlinedInput-root': { height: 30, fontSize: '12px' } }}
                                                                    />
                                                                </td>
                                                                <td style={{ padding: '6px 8px', borderRight: `1px solid ${themeColors.neutral.line}`, verticalAlign: 'middle' }}>
                                                                    {charge.TaxEnabled ? (
                                                                        <Dropdown
                                                                            id={`charge-tax-${key}`}
                                                                            valueKey="Id"
                                                                            labelKey="value"
                                                                            value={input.taxId ? String(input.taxId) : ""}
                                                                            options={gsts}
                                                                            onChange={(opt) => updateChargeInput(key, { taxId: opt ? opt.Id : null })}
                                                                        />
                                                                    ) : (
                                                                        <Typography variant="caption" sx={{ color: themeColors.neutral.muted, fontSize: '12px' }}>N/A</Typography>
                                                                    )}
                                                                </td>
                                                                <td style={{ padding: '6px 8px', verticalAlign: 'middle' }}>
                                                                    <TextField
                                                                        size="small"
                                                                        fullWidth
                                                                        type="number"
                                                                        value={finalAmount.toFixed(2)}
                                                                        disabled
                                                                        sx={{ '& .MuiOutlinedInput-root': { height: 30, backgroundColor: themeColors.neutral.surfaceMuted, fontSize: '12px' } }}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </Box>
                                    </AccordionDetails>
                                </Accordion>
                            </Box>
                        )}

                        {state?.toLowerCase() === compState?.toLowerCase() ? (
                            <>
                                <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography>CGST</Typography>
                                    <Typography>₹{formatINR(Number(totals.cgst) + chargesSummary.cgst)}</Typography>
                                </Grid>
                                <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography>SGST</Typography>
                                    <Typography>₹{formatINR(Number(totals.sgst) + chargesSummary.sgst)}</Typography>
                                </Grid>
                            </>
                        ) : (
                            totals.igst > 0 && (
                                <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography>IGST</Typography>
                                    <Typography>₹{formatINR(Number(totals.igst) + chargesSummary.igst)}</Typography>
                                </Grid>
                            )
                        )}
                        <Grid container justifyContent="space-between" alignItems="center" mb={1}>
                            <Typography>Round Off ({Number(totals.roundOff) >= 0 ? '+' : '-'})</Typography>
                            <Typography>₹{formatINR(totals.roundOff)}</Typography>
                        </Grid>

                        <Divider sx={{ my: 1 }} />
                        <Grid container justifyContent="space-between" alignItems="center">
                            <Typography fontWeight={600}>Total Amount</Typography>
                            <Typography fontWeight={600}>₹{formatINR(Number(totals.total))}</Typography>
                        </Grid>
                    </Box>
                </Box>
            </Box>
            {!!errors.global && (
                <Typography color="error" sx={{ mt: 2 }}>
                    {errors.global}
                </Typography>
            )}
            <Box mt={1} display="flex" justifyContent="flex-end">
                <Button
                    variant="contained"
                    onClick={handleNext}
                    disabled={isEditingMode}   // 👉 NEW: block Next while editing
                >
                    Next
                </Button>

            </Box>


            <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>

        </Paper>
    );
};

export default ProductDetails;